// noinspection SpellCheckingInspection

import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExitIcon from "@mui/icons-material/TransitEnterexit";
import UndoIcon from "@mui/icons-material/Undo";
import { keyframes } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import { green, lightBlue } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo } from "react";
import { useSession } from "users/__session/session-context";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import DiffText from "work/proposals/redlining/__diff/view/diff-text";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import VendorTeamMemberSelection from "work/work-agreements/teams/vendor-team/_views/members/vendor-team-member-selection";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
const TeamList = styled(List)(({ theme }) => ({
  margin: 0,
  marginTop: 0,
  minWidth: theme.spacing(42),
  padding: theme.spacing(1),
  paddingBottom: theme.spacing(3),
  "&:first-of-type": {
    marginRight: theme.spacing(3),
  },
  [theme.breakpoints.down("lg")]: {
    "&:first-of-type": {
      marginRight: 0,
    },
  },
}));
const TeamListSubheader = styled(ListSubheader)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
  display: "flex",
  fontSize: "1.3em",
  justifyContent: "space-between",
  padding: 0,
  "& > button": {
    height: "fit-content",
    width: "fit-content",
  },
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  paddingTop: theme.spacing(1),
}));
const TeamMemberLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  cursor: "pointer",
  textDecoration: "underline",
  textDecorationColor: theme.palette.common.black,
}));
const MemberAvatar = styled(IndividualAvatar, {
  shouldForwardProp: (prop) => !["removed", "isnewlyremoved", "added", "resolved"].includes(prop.toString()),
})<{
  removed?: boolean;
  isnewlyremoved: boolean;
  added?: boolean;
  resolved?: boolean;
}>(({ removed, isnewlyremoved, added, resolved, theme }) => ({
  backgroundColor: (function () {
    if ((isnewlyremoved || added) && resolved) return lightBlue[700];
    if (removed && !resolved) return theme.palette.error.main;
    if (removed && resolved) return theme.palette.common.black;
    if (added) return green[300];
    return theme.palette.grey[400];
  })(),
}));
const DefaultMemberAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => !["removed", "isnewlyremoved", "added", "resolved"].includes(prop.toString()),
})<{
  removed?: boolean;
  isnewlyremoved: boolean;
  added?: boolean;
  resolved?: boolean;
}>(({ removed, isnewlyremoved, added, resolved, theme }) => ({
  backgroundColor: (function () {
    if ((isnewlyremoved || added) && resolved) return lightBlue[700];
    if (removed && !resolved) return theme.palette.error.main;
    if (removed && resolved) return theme.palette.common.black;
    if (added) return green[300];
    return theme.palette.grey[400];
  })(),
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const RedlinedLink = styled(DiffText)(({ theme }) => ({
  "&:hover span": {
    textDecoration: "underline",
    textDecorationColor: theme.palette.common.black,
  },
}));
const RemovedMemberDiff = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isnewlyremoved",
})<{ isnewlyremoved: boolean }>(({ theme, isnewlyremoved }) => ({
  color: theme.palette.text.disabled,
  textDecoration: "line-through",
  textDecorationColor: isnewlyremoved ? lightBlue[700] : theme.palette.common.black,
}));
const MemberListItem = styled(ListItem, { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s infinite` : "none",
    border: active ? "1px solid" : 0,
    borderColor: theme.palette.primary.main,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
    paddingLeft: active ? theme.spacing(1) : 0,
  }),
);

type TeamRedlineSelectionProps = {
  className?: string;
  activeReviewField?: ProposalField;
  teamLeader?: EntityMember;
  teamMembersRedline: FieldRedlineArray<EntityMember>;
  disableEditing?: boolean;
  commentThreads?: CommentThread[];
  onCommentsClicked?: (field: ProposalField, title?: string) => void;
  onTeamMembersRedlineChange: (
    newTeamMembersRedline: FieldRedlineArray<EntityMember>,
    traversalFieldOverride?: ProposalField | null,
  ) => void;
  onMemberQuitTeam: (memberId: Guid) => void;
  onMemberClicked: (id: Guid) => void;
};

export default function TeamRedlineSelection(props: Readonly<TeamRedlineSelectionProps>) {
  const {
    className,
    activeReviewField,
    teamLeader,
    teamMembersRedline,
    disableEditing,
    commentThreads,
    onCommentsClicked,
    onTeamMembersRedlineChange,
    onMemberQuitTeam,
    onMemberClicked,
  } = props;

  // #region Hooks
  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();
  const session = useSession();

  const originalTeamMemberUserIds: string[] = useMemo(() => {
    return teamMembersRedline.redlines
      .map((memberRedline) => memberRedline.originalEntry?.userId.value)
      .filter((id) => id !== undefined);
  }, [teamMembersRedline]);

  const revisedTeamMemberUserIds: string[] = useMemo(() => {
    return teamMembersRedline.redlines
      .map((memberRedline) => memberRedline.revisedEntry?.userId.value)
      .filter((id) => id !== undefined);
  }, [teamMembersRedline]);

  const currentTeamMemberUserIds: string[] = useMemo(() => {
    return teamMembersRedline.redlines
      .map((memberRedline) => memberRedline.currentEntry?.userId.value)
      .filter((id) => id !== undefined);
  }, [teamMembersRedline]);

  const allTeamMemberUserIds: string[] = useMemo(() =>
    [
      teamLeader?.userId.value,
      ...originalTeamMemberUserIds,
      ...currentTeamMemberUserIds,
      ...revisedTeamMemberUserIds,
    ].filter((id) => id !== undefined),
    [teamLeader, originalTeamMemberUserIds, currentTeamMemberUserIds, revisedTeamMemberUserIds],
  );

  const sortedUniqueTeamMemberUserIds: Set<string> = useMemo(() => {
    return new Set(allTeamMemberUserIds);
  }, [allTeamMemberUserIds]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    sortedUniqueTeamMemberUserIds.size ? { userIds: sortedUniqueTeamMemberUserIds } : skipToken
  );

  const teamMemberIndividuals: Individual[] = useMemo(() => {
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const teamMemberMap: Map<string, Individual> = useMemo(() => {
    const map = new Map<string, Individual>();
    for (const individual of teamMemberIndividuals) {
      map.set(individual.userId.value, individual);
    }
    return map;
  }, [teamMemberIndividuals]);

  useEffect(() => {
    const target = document.getElementById(activeReviewField?.id?.value ?? "");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeReviewField]);

  /**
   * Checks if the user with the given id is already selected or invited to the vendor-team
   *
   * @param userId Id of the user to check
   * @returns True if the user is already selected or invited to the vendor-team, false otherwise
   */
  function getIsMemberAlreadyOnTeam(userId?: Guid): boolean {
    if (!userId) return false;

    const memberUserIds = teamMembersRedline?.redlines.map((memberRedline) =>
      memberRedline.currentEntry ? memberRedline.currentEntry.userId : undefined,
    );

    return (
      (memberUserIds?.some((memberUserId) => memberUserId?.isEqualTo(userId)) ?? false) ||
      (teamLeader?.userId?.isEqualTo(userId) ?? false)
    );
  }

  function getSelectedTeamMemberUserIds(): Guid[] {
    const userIds: Guid[] =
      teamMembersRedline?.redlines
        .map((memberRedline) => memberRedline.currentEntry?.userId)
        .filter((id) => id != undefined) ?? [];
    if (teamLeader?.userId) {
      userIds.push(teamLeader.userId);
    }
    return userIds;
  }

  /**
   * Handles what happens when add member is clicked
   */
  function beginMemberSelection() {
    openDialog({
      title: "Select Member",
      component: (
        <VendorTeamMemberSelection
          onNewSelection={handleNewMemberSelected}
          selectLeader={false}
          selectMember={true}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  /**
   * Handles when a new member has been selected
   *
   * @param newMember The new member to add to the vendor-team
   */
  function handleNewMemberSelected(newMember: Individual) {
    if (getIsMemberAlreadyOnTeam(newMember.userId)) {
      enqueueSnackbar("Selected user is already on vendor-team", {
        variant: "info",
        preventDuplicate: true,
        autoHideDuration: 7500,
      });
      return;
    }

    try {
      onTeamMembersRedlineChange(
        teamMembersRedline.addEntry(newMember.toEntityMember()),
        ProposalField.TeamMember(newMember.id),
      );
    } catch (error) {
      console.error("Error adding new member to vendor-team", error);
      enqueueSnackbar("Error adding new member to vendor-team", {
        variant: "error",
      });
    }
  }

  function handleMemberRemoved(memberId: Guid) {
    try {
      onTeamMembersRedlineChange(teamMembersRedline.removeEntryByFieldId(memberId), null);
    } catch (error) {
      console.error("Error removing member from vendor-team", error);
      enqueueSnackbar("Error removing member from vendor-team", {
        variant: "error",
      });
    }
  }

  async function handleQuitTeam(memberId?: Guid) {
    if (!memberId) return;

    const response = await confirm({
      title: "Leave Proposal?",
      message: `Do you want to leave this proposal?`,
      okButtonText: "Leave",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      onMemberQuitTeam(memberId);
    } catch (error) {
      console.error("Error quitting vendor-team", error);
      enqueueSnackbar("Error quitting vendor-team", {
        variant: "error",
      });
    }
  }

  function openUserProfileDialog(individualId?: Guid) {
    if (!individualId) return;
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  function handleUndoChanges(teamMemberRedline: FieldRedline<EntityMember>) {
    if (!teamMemberRedline.field.id) {
      console.error("Field ID is missing for vendor-team member redline");
      enqueueSnackbar("Error undoing changes", { variant: "error" });
      return;
    }
    try {
      onTeamMembersRedlineChange(
        teamMembersRedline.undoRedlineById(teamMemberRedline.field.id),
        teamMemberRedline.isNewlyAdded ? null : teamMemberRedline.field,
      );
    } catch (error) {
      console.error("Error undoing changes", error);
      enqueueSnackbar("Error undoing changes", { variant: "error" });
    }
  }

  function handleUndoAllChanges() {
    try {
      onTeamMembersRedlineChange(teamMembersRedline.undoAll(), null);
    } catch (error) {
      console.error("Error undoing all changes", error);
      enqueueSnackbar("Error undoing all changes", { variant: "error" });
    }
  }

  async function handleRemoveAllMembers() {
    const response = await confirm({
      title: "Remove All Members?",
      message: "Do you want to remove all members from the vendor-team?",
      okButtonText: "Remove All",
    });

    if (response === ConfirmResponse.Cancel) return;
    try {
      onTeamMembersRedlineChange(teamMembersRedline.removeAll(), null);
    } catch (error) {
      console.error("Error removing all members from vendor-team", error);
      enqueueSnackbar("Error removing all members from vendor-team", {
        variant: "error",
      });
    }
  }

  function handleRejectAllChanges() {
    try {
      onTeamMembersRedlineChange(teamMembersRedline.rejectAll());
    } catch (error) {
      console.error("Error rejecting all changes", error);
      enqueueSnackbar("Error rejecting all changes", { variant: "error" });
    }
  }

  function handleAcceptAllChanges() {
    try {
      onTeamMembersRedlineChange(teamMembersRedline.acceptAll());
    } catch (error) {
      console.error("Error accepting all changes", error);
      enqueueSnackbar("Error accepting all changes", { variant: "error" });
    }
  }

  const getActionButtonTooltipText = (isAdded: boolean, isRemoved: boolean, accept: boolean) => {
    const verb = accept ? "Accept " : "Reject ";

    if (isAdded) return `${verb} New Member`;
    if (isRemoved) return `${verb} Removed Member`;
    return `${verb} All Changes`;
  };

  function renderTeamMemberRedline(teamMemberRedline: FieldRedline<EntityMember>) {
    if (!teamMemberRedline.field.id) {
      console.error("Field ID is missing for vendor-team member redline");
      return;
    }

    const originalIndividual = teamMemberMap.get(teamMemberRedline.originalEntry?.userId.value ?? "");
    const revisedIndividual = teamMemberMap.get(teamMemberRedline.revisedEntry?.userId.value ?? "");
    const currentIndividual = teamMemberMap.get(teamMemberRedline.currentEntry?.userId.value ?? "");

    return (
      <MemberListItem
        id={teamMemberRedline.field.id.value}
        key={teamMemberRedline.field.id.value}
        active={activeReviewField?.id?.isEqualTo(teamMemberRedline.field.id) ?? false}
        disableGutters
        disablePadding
        onClick={() => {
          if (!teamMemberRedline.field.id) return;
          onMemberClicked(teamMemberRedline.field.id);
        }}
        secondaryAction={
          <>
            {teamMemberRedline.currentEntry?.userId?.isEqualTo(session.user?.id) && (
              <Tooltip title="Quit Team">
                <span>
                  <ActionButton onClick={() => handleQuitTeam(teamMemberRedline.currentEntry?.userId ?? undefined)}>
                    <ExitIcon />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            {!disableEditing && (
              <>
                {teamMemberRedline.isResolved && !teamMemberRedline.isRemoved && !teamMemberRedline.canBeUndone && (
                  <Tooltip title="Remove Member">
                    <span>
                      <ActionButton
                        onClick={() => {
                          if (teamMemberRedline.currentEntry) {
                            handleMemberRemoved(teamMemberRedline.currentEntry?.id ?? undefined);
                          }
                        }}
                      >
                        <DeleteOutlineIcon color="error" />
                      </ActionButton>
                    </span>
                  </Tooltip>
                )}
                {teamMemberRedline.canBeUndone && (
                  <Tooltip title="Undo Changes">
                    <span>
                      <ActionButton
                        onClick={() => {
                          try {
                            handleUndoChanges(teamMemberRedline);
                          } catch (error) {
                            console.error("Error undoing changes", error);
                            enqueueSnackbar("Error undoing changes", {
                              variant: "error",
                            });
                          }
                        }}
                      >
                        <UndoIcon color="secondary" />
                      </ActionButton>
                    </span>
                  </Tooltip>
                )}
                {!teamMemberRedline.isResolved && (teamMemberRedline.isAdded || teamMemberRedline.isRemoved) && (
                  <>
                    <Tooltip
                      title={getActionButtonTooltipText(teamMemberRedline.isAdded, teamMemberRedline.isRemoved, true)}
                    >
                      <span>
                        <ActionButton
                          onClick={() => {
                            if (!teamMemberRedline.field.id) {
                              console.error("Field ID is missing for vendor-team member redline");
                              return;
                            }
                            try {
                              onTeamMembersRedlineChange(
                                teamMembersRedline.acceptRedlineById(teamMemberRedline.field.id),
                              );
                            } catch (error) {
                              console.error("Error accepting changes", error);
                              enqueueSnackbar("Error accepting changes", {
                                variant: "error",
                              });
                            }
                          }}
                        >
                          <CheckIcon color="success" />
                        </ActionButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={getActionButtonTooltipText(teamMemberRedline.isAdded, teamMemberRedline.isRemoved, false)}
                    >
                      <span>
                        <ActionButton
                          onClick={() => {
                            if (!teamMemberRedline.field.id) {
                              console.error("Field ID is missing for vendor-team member redline");
                              enqueueSnackbar("Error rejecting changes", {
                                variant: "error",
                              });
                              return;
                            }
                            try {
                              onTeamMembersRedlineChange(
                                teamMembersRedline.rejectRedlineById(teamMemberRedline.field.id),
                              );
                            } catch (error) {
                              console.error("Error rejecting changes", error);
                              enqueueSnackbar("Error rejecting changes", {
                                variant: "error",
                              });
                            }
                          }}
                        >
                          <CloseIcon color="error" />
                        </ActionButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </>
            )}
            <CommentsButton
              field={teamMemberRedline.field}
              commentThreads={commentThreads}
              isActive={activeReviewField?.id?.isEqualTo(teamMemberRedline.field.id)}
              toolTip={"Comments"}
              onCommentsClicked={
                onCommentsClicked
                  ? () => onCommentsClicked(teamMemberRedline.field, revisedIndividual?.fullName)
                  : undefined
              }
            />
          </>
        }
      >
        <ListItemAvatar>
          {teamMemberRedline.currentEntry ? (
            <MemberAvatar
              individual={currentIndividual}
              added={teamMemberRedline.isAdded}
              isnewlyremoved={teamMemberRedline.isNewlyRemoved}
              removed={teamMemberRedline.isRemoved}
              resolved={teamMemberRedline.isResolved}
              session={session}
            />
          ) : (
            <DefaultMemberAvatar
              added={teamMemberRedline.isAdded}
              isnewlyremoved={teamMemberRedline.isNewlyRemoved}
              removed={teamMemberRedline.isRemoved}
              resolved={teamMemberRedline.isResolved}
            />
          )}
        </ListItemAvatar>
        <ListItemText
          primary={
            teamMemberRedline.isRemoved && teamMemberRedline.isResolved ? (
              <RemovedMemberDiff isnewlyremoved={teamMemberRedline.isNewlyRemoved}>
                {revisedIndividual?.fullName ?? originalIndividual?.fullName}
              </RemovedMemberDiff>
            ) : (
              <TeamMemberLink
                onClick={() =>
                  openUserProfileDialog(
                    teamMemberRedline.currentEntry?.id ??
                    teamMemberRedline.revisedEntry?.id ??
                    teamMemberRedline.originalEntry?.id ??
                    undefined,
                  )
                }
              >
                {individualQuery.isLoading && ("Loading...")}
                {!individualQuery.isLoading && individualQuery.isError && (
                  <Typography color="error">Error loading</Typography>
                )}
                {!individualQuery.isLoading && individualQuery.isSuccess && (
                  <RedlinedLink
                    textChanges={
                      TextChangeCollection.fromRedlines(
                        {
                          originalEntry: originalIndividual ?? null,
                          revisedEntry: revisedIndividual ?? null,
                          currentEntry: currentIndividual ?? null,
                        },
                        { diffWords: false } 
                      )?.textChanges ?? []
                    }
                    allResolved={teamMemberRedline.isResolved}
                    disablePopover={true}
                  />
                )}
              </TeamMemberLink>
            )
          }
        ></ListItemText>
      </MemberListItem>
    );
  }

  function renderLeader() {
    if (!teamLeader) return null;
    const leaderIndividual = teamMemberMap.get(teamLeader.userId.value);
    return (
      <ListItem
        disableGutters
        secondaryAction={
          <CommentsButton
            field={ProposalField.TeamLeader}
            commentThreads={commentThreads}
            isActive={activeReviewField?.isEqualTo(ProposalField.TeamLeader)}
            toolTip={"Comments"}
            onCommentsClicked={
              onCommentsClicked
                ? () => {
                  onCommentsClicked(ProposalField.TeamLeader, leaderIndividual?.fullName);
                }
                : undefined
            }
          />
        }
      >
        <ListItemAvatar>
          <IndividualAvatar individual={leaderIndividual} session={session} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <TeamMemberLink
              sx={{
                opacity: individualQuery.isLoading ? 0.75 : 1,
                pointerEvents: individualQuery.isLoading ? "none" : "auto",
                textDecoration: individualQuery.isLoading ? "none" : "underline",
              }}
              onClick={() => openUserProfileDialog(leaderIndividual?.id ?? undefined)}
            >
              {individualQuery.isLoading ? "Loading..." : leaderIndividual?.fullName}
            </TeamMemberLink>
          }
        ></ListItemText>
      </ListItem>
    );
  }

  return (
    <MainContainer className={className}>
      <TeamContainer>
        <TeamList>
          <TeamListSubheader>Team Leader</TeamListSubheader>
          {renderLeader()}
        </TeamList>
        <TeamList>
          <TeamListSubheader>
            <span>Team Members</span>
            <div>
              {!disableEditing && (
                <>
                  {teamMembersRedline.isResolved && !teamMembersRedline.isEmpty && (
                    <Tooltip title="Remove All Members">
                      <span>
                        <ActionButton onClick={handleRemoveAllMembers}>
                          <DeleteOutlineIcon color="error" />
                        </ActionButton>
                      </span>
                    </Tooltip>
                  )}
                  {!teamMembersRedline.isResolved && (
                    <>
                      <Tooltip title="Accept All Changes">
                        <span>
                          <ActionButton onClick={() => handleAcceptAllChanges()}>
                            <CheckIcon color="success" />
                          </ActionButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reject All Changes">
                        <span>
                          <ActionButton onClick={() => handleRejectAllChanges()}>
                            <CloseIcon color="error" />
                          </ActionButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  {teamMembersRedline.redlines.some((r) => r.canBeUndone) && (
                    <Tooltip title="Undo All">
                      <span>
                        <ActionButton onClick={() => handleUndoAllChanges()}>
                          <UndoIcon color="secondary" />
                        </ActionButton>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
              <CommentsButton
                field={ProposalField.Team}
                toolTip={"Comments"}
                commentThreads={commentThreads}
                isActive={activeReviewField?.id?.isEqualTo(ProposalField.Team.id)}
                onCommentsClicked={
                  onCommentsClicked
                    ? () => {
                      onCommentsClicked(ProposalField.Team);
                    }
                    : undefined
                }
              />
            </div>
          </TeamListSubheader>
          {!disableEditing && (
            <ListItemButton disableGutters dense onClick={beginMemberSelection}>
              <ListItemAvatar>
                <AddIcon color="primary" />
              </ListItemAvatar>
              <ListItemText>
                <Typography color="primary">Add Member</Typography>
              </ListItemText>
            </ListItemButton>
          )}
          {(!teamMembersRedline || teamMembersRedline.redlines.length < 1) && (
            <ListItem disableGutters>
              <NoRowsPlaceholder color="textDisabled">No Members</NoRowsPlaceholder>
            </ListItem>
          )}
          {teamMembersRedline?.redlines.map((member: FieldRedline<EntityMember>) => renderTeamMemberRedline(member))}
        </TeamList>
      </TeamContainer>
    </MainContainer>
  );
}
